/**
 * V2TextFieldSection — V2-native editor section for step.content.textField
 *
 * Edits: fieldType, fieldSubtype, placeholder, validationRegex, errorMessage
 * V2 paths: content.textField.*
 *
 * Replaces: OnboardingFieldSection (for V2 context)
 * Reuses: TextElement, SAccordion, EditorGuideProvider
 *
 * Sprint: schema-v2-migration, Sprint 4
 */

import React, { useCallback, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  Collapse,
  IconButton,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { SAccordion, StyledTextField } from '@modules/ui-components';
import { TextElement } from '../../../factories/elements/TextElement';
import { ImageUploadElement } from '../../../factories/elements/ImageUploadElement';
import { StyleFieldRow } from '../../shared/styleFields/StyleFieldRow';
import { EditorGuideProvider } from '../../../contexts';
import { OnboardingMediaUploadService } from '@modules/firebase-core/services/OnboardingMediaUploadService';
import { toV2AssetReference, readAssetUrl } from '@modules/reactFlow/utils/v2AssetUtils';
import {
  V2_FIELD_TYPE_OPTIONS,
  V2_KEYBOARD_TYPE_OPTIONS,
  V2_PLACEHOLDER_CONFIG,
  V2_ERROR_MESSAGE_CONFIG,
  V2_VALIDATION_REGEX_CONFIG,
  V2_FIELD_STYLES_CONFIG,
  V2_ICON_CONFIG,
  V2_AUTO_FOCUS_CONFIG,
} from '../../../config/v2TextFieldConfig';
import { withScreenEditorFieldGuide, withScreenEditorFieldGuides } from '../../../config/screenEditorFieldGuideResolver';
import { PaddingElement, type PaddingValues } from '../../../factories/elements';
import { SECTION_MARGIN_BOTTOM, FIELD_MARGIN_BOTTOM, FIELD_GAP_SMALL, FIELD_GAP, FIELD_STYLE_GRID_GAP, FIELD_STYLE_MARGIN_TOP } from '../../../config/ui';
import {
  fieldSelectSx,
  fieldMenuItemSx,
  mainFieldLabelEnabledSx,
  mainFieldLabelDisabledSx,
  checkboxSx,
  checkboxIconSx,
  checkboxCheckedIconSx,
  expandedToggleButtonSx,
  expandedToggleButtonActiveSx,
  expandedSectionSx,
} from '../../../config/editorFieldStyles';
import { getValueByPath, isFieldEnabled } from '../../../utils/v2FieldOperations';
import { getV2TranslationValue } from '../../../utils/v2FieldOperations';
import {
  getStyleValues as getStyleValuesUtil,
} from '../../../utils/fieldValueOperations';
import { useEditorPresentationProfile } from './PresentationProfileContext';
import type { FieldConfig } from '../../../types/editorFieldTypes';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import type { OnboardingThemeSettingsV2 } from '@modules/firebase-core/converters/onboardingConverter';
import { getThemedTextFieldDefaults } from '../../../utils/v2ThemeDefaults';
import {
  getSectionTestId,
  getFieldTestId,
  getCheckboxTestId,
  getStylesToggleTestId,
  getInputTestId,
  getStyleInputTestId,
} from '../../../utils/testIdUtils';

export interface V2TextFieldSectionProps {
  step: FlowStepNode;
  locale: string;
  onChange: (path: string, value: unknown) => void;
  disabled?: boolean;
  defaultExpanded?: boolean;
  projectId?: string;
  onboardingId?: string;
  versionId?: string;
  /** Render function for embedded subsections (e.g. Variable Binding) */
  renderSubsections?: () => React.ReactNode;
  /** Badge count from subsections to aggregate with own badge */
  subsectionBadgeCount?: number;
  /** Where to render subsections relative to own content */
  subsectionPosition?: 'first' | 'last';

  /** Theme settings for themed defaults on element toggle ON */
  themeSettingsV2?: OnboardingThemeSettingsV2 | null;
}

export const V2TextFieldSection: React.FC<V2TextFieldSectionProps> = ({
  step,
  locale,
  onChange,
  disabled = false,
  defaultExpanded = true,
  projectId,
  onboardingId,
  versionId,
  renderSubsections,
  subsectionBadgeCount,
  subsectionPosition,
  themeSettingsV2,
}) => {
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());
  const [fieldStylesExpanded, setFieldStylesExpanded] = useState(false);
  const uploadService = useMemo(() => new OnboardingMediaUploadService(), []);

  const toggleFieldExpansion = useCallback((path: string) => {
    setExpandedFields(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  // Read text value using V2 translations path
  const getFieldValue = useCallback(
    (config: FieldConfig): string => {
      if (config.l10nPath) {
        return getV2TranslationValue(step, config.l10nPath, locale);
      }
      return getValueByPath<string>(step, config.path) ?? '';
    },
    [step, locale]
  );

  const checkFieldEnabled = useCallback(
    (config: FieldConfig): boolean => isFieldEnabled(step, config.path),
    [step]
  );

  const activeProfile = useEditorPresentationProfile();

  const getStyleValues = useCallback(
    (config: FieldConfig): Record<string, unknown> => getStyleValuesUtil(step as unknown as Record<string, unknown>, config, activeProfile),
    [step, activeProfile]
  );

  // Field appearance style values (backgroundColor, borderColor, enteredTextColor)
  const fieldStyleValues = useMemo(() => {
    const values: Record<string, unknown> = {};
    for (const sf of V2_FIELD_STYLES_CONFIG.styleFields ?? []) {
      values[sf.path] = getValueByPath(step, sf.path);
    }
    return values;
  }, [step]);

  // Icon (LocalizedImage) — read asset reference, extract URL for ImageUploadElement
  const iconEnabled = useMemo(() => isFieldEnabled(step, V2_ICON_CONFIG.path), [step]);
  const iconUrl = useMemo(() => {
    const assetRef = getValueByPath<Record<string, string>>(step, `${V2_ICON_CONFIG.path}.translations.${locale}`);
    return readAssetUrl(assetRef as Parameters<typeof readAssetUrl>[0]);
  }, [step, locale]);
  const iconStyleValues = useMemo(() => {
    const values: Record<string, unknown> = {};
    for (const sf of V2_ICON_CONFIG.styleFields ?? []) {
      values[sf.path] = getValueByPath(step, sf.path);
    }
    return values;
  }, [step]);

  const handleIconEnabledChange = useCallback(
    (enabled: boolean) => {
      if (enabled) {
        onChange(V2_ICON_CONFIG.path, { translations: { [locale]: null }, appearance: {} });
      } else {
        onChange(V2_ICON_CONFIG.path, null);
      }
    },
    [onChange, locale]
  );

  const handleIconChange = useCallback(
    (value: string | null) => {
      if (value) {
        onChange(`${V2_ICON_CONFIG.path}.translations.${locale}`, toV2AssetReference({ origin: value }));
      } else {
        onChange(`${V2_ICON_CONFIG.path}.translations.${locale}`, null);
      }
    },
    [onChange, locale]
  );

  const handleIconUpload = useCallback(
    async (file: File): Promise<string> => {
      const result = await uploadService.uploadImage(file, {
        projectId: projectId!,
        onboardingId: onboardingId!,
        versionId: versionId!,
        locale,
      });
      onChange(`${V2_ICON_CONFIG.path}.translations.${locale}`, toV2AssetReference(result.assetUrl));
      return result.assetUrl.origin;
    },
    [uploadService, projectId, onboardingId, versionId, locale, onChange]
  );

  // Write text value to V2 translations path
  const handleFieldChange = useCallback(
    (config: FieldConfig, value: string) => {
      if (config.l10nPath) {
        onChange(`${config.l10nPath}.translations.${locale}`, value);
      } else {
        onChange(config.path, value);
      }
    },
    [onChange, locale]
  );

  const handleEnabledChange = useCallback(
    (config: FieldConfig, enabled: boolean) => {
      if (enabled) {
        const defaultText = (config.defaultValue as string) || '';
        if (themeSettingsV2) {
          onChange(config.path, getThemedTextFieldDefaults(themeSettingsV2, locale, defaultText));
        } else {
          onChange(config.path, {
            kind: 'static',
            translations: { [locale]: defaultText },
            appearance: {},
          });
        }
      } else {
        onChange(config.path, null);
      }
    },
    [onChange, locale, themeSettingsV2]
  );

  const handleStyleChange = useCallback(
    (stylePath: string, value: unknown) => onChange(stylePath, value),
    [onChange]
  );

  // Field type logic — 'email' is now a first-class FieldType (schema 2.7.0)
  // Backward compat: fieldType='string' + fieldSubtype='Email' treated as email
  const fieldType = getValueByPath<string>(step, 'content.textField.fieldType') ?? 'string';
  const fieldSubtype = getValueByPath<string>(step, 'content.textField.fieldSubtype');
  const isLegacyEmail = fieldType === 'string' && fieldSubtype === 'Email';
  const displayFieldType = isLegacyEmail ? 'email' : fieldType;

  const handleFieldTypeChange = useCallback(
    (event: { target: { value: unknown } }) => {
      const selectedValue = event.target.value as string;
      onChange('content.textField.fieldType', selectedValue);
      // Clear legacy fieldSubtype when switching away from email
      if (fieldSubtype) {
        onChange('content.textField.fieldSubtype', null);
      }
    },
    [onChange, fieldSubtype]
  );

  // Keyboard type — explicit override or auto-derived
  const keyboardType = getValueByPath<string>(step, 'content.textField.keyboardType') ?? '';

  const handleKeyboardTypeChange = useCallback(
    (event: { target: { value: unknown } }) => {
      const val = event.target.value as string;
      onChange('content.textField.keyboardType', val || null);
    },
    [onChange]
  );

  // Validation regex
  const validationRegexRaw = getValueByPath<string>(step, 'content.textField.validationRegex');
  const hasValidationRegex = validationRegexRaw !== null && validationRegexRaw !== undefined;
  const validationRegex = validationRegexRaw ?? '';

  // Memoized props
  const placeholderProps = useMemo(() => ({
    fieldConfig: withScreenEditorFieldGuide(V2_PLACEHOLDER_CONFIG, 'textField'),
    value: getFieldValue(V2_PLACEHOLDER_CONFIG),
    templateValue: undefined,
    enabled: checkFieldEnabled(V2_PLACEHOLDER_CONFIG),
    locale,
    styleValues: getStyleValues(V2_PLACEHOLDER_CONFIG),
    expandedStyles: expandedFields.has(V2_PLACEHOLDER_CONFIG.path),
  }), [getFieldValue, checkFieldEnabled, locale, getStyleValues, expandedFields]);

  const errorMessageProps = useMemo(() => ({
    fieldConfig: withScreenEditorFieldGuide(V2_ERROR_MESSAGE_CONFIG, 'textField'),
    value: getFieldValue(V2_ERROR_MESSAGE_CONFIG),
    templateValue: undefined,
    enabled: checkFieldEnabled(V2_ERROR_MESSAGE_CONFIG),
    locale,
    styleValues: getStyleValues(V2_ERROR_MESSAGE_CONFIG),
    expandedStyles: expandedFields.has(V2_ERROR_MESSAGE_CONFIG.path),
  }), [getFieldValue, checkFieldEnabled, locale, getStyleValues, expandedFields]);

  // Auto-focus on appear
  const autoFocusRaw = getValueByPath<string | boolean>(step, V2_AUTO_FOCUS_CONFIG.path);
  const autoFocusValue = autoFocusRaw === null || autoFocusRaw === undefined ? 'true' : String(autoFocusRaw);

  const handleAutoFocusChange = useCallback(
    (newValue: unknown) => {
      if (newValue === null || newValue === undefined) {
        onChange(V2_AUTO_FOCUS_CONFIG.path, null);
      } else {
        onChange(V2_AUTO_FOCUS_CONFIG.path, newValue === 'true');
      }
    },
    [onChange]
  );

  // Field padding (EdgeInsets: top/end/bottom/start → PaddingValues: paddingTop/Right/Bottom/Left)
  const paddingValues = useMemo((): PaddingValues => {
    const insets = getValueByPath<Record<string, number>>(step, 'content.textField.layout.insets');
    return {
      paddingTop: typeof insets?.top === 'number' ? insets.top : 0,
      paddingRight: typeof insets?.end === 'number' ? insets.end : 0,
      paddingBottom: typeof insets?.bottom === 'number' ? insets.bottom : 0,
      paddingLeft: typeof insets?.start === 'number' ? insets.start : 0,
    };
  }, [step]);

  // Border margins (EdgeInsets)
  const borderMarginsValues = useMemo((): PaddingValues => {
    const insets = getValueByPath<Record<string, number>>(step, 'content.textField.appearance.borderMargins');
    return {
      paddingTop: typeof insets?.top === 'number' ? insets.top : 0,
      paddingRight: typeof insets?.end === 'number' ? insets.end : 0,
      paddingBottom: typeof insets?.bottom === 'number' ? insets.bottom : 0,
      paddingLeft: typeof insets?.start === 'number' ? insets.start : 0,
    };
  }, [step]);

  const handlePaddingChange = useCallback(
    (path: string, value: number) => {
      // Translate PaddingElement paths (paddingRight → end, paddingLeft → start)
      const translated = path
        .replace('.paddingTop', '.top')
        .replace('.paddingRight', '.end')
        .replace('.paddingBottom', '.bottom')
        .replace('.paddingLeft', '.start');
      onChange(translated, value);
    },
    [onChange]
  );

  const SECTION_NAME = 'Text Field';

  return (
    <SAccordion
      title="Text Field"
      collapsible
      defaultExpanded={defaultExpanded}
      badge={subsectionBadgeCount || undefined}
      badgeColor={subsectionBadgeCount ? 'warning' : undefined}
      sx={{ marginBottom: SECTION_MARGIN_BOTTOM }}
      data-testid={getSectionTestId(SECTION_NAME)}
    >
      {subsectionPosition === 'first' && renderSubsections?.()}
      <EditorGuideProvider>
        {/* Field Type Selector + Field Styles Gear */}
        <Box sx={{ mb: FIELD_MARGIN_BOTTOM }}>
          <Typography
            variant="body2"
            sx={{ ...mainFieldLabelEnabledSx, mb: FIELD_GAP_SMALL }}
          >
            Field Type
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: FIELD_GAP }}>
            <FormControl size="small" sx={{ flex: 1 }} disabled={disabled} variant="standard">
              <Select
                value={displayFieldType}
                onChange={handleFieldTypeChange as any}
                displayEmpty
                sx={fieldSelectSx}
                data-testid={getInputTestId(SECTION_NAME, 'Field Type')}
              >
                {V2_FIELD_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value} sx={fieldMenuItemSx}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton
              onClick={() => setFieldStylesExpanded((prev) => !prev)}
              size="small"
              disabled={disabled}
              aria-label="Toggle field style settings"
              data-testid={getStylesToggleTestId(SECTION_NAME, V2_FIELD_STYLES_CONFIG.label)}
              sx={fieldStylesExpanded ? expandedToggleButtonActiveSx : expandedToggleButtonSx}
            >
              <TuneIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Field Styles Panel (backgroundColor, borderColor, enteredTextColor, keyboardType) */}
          <Collapse in={fieldStylesExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ mt: FIELD_STYLE_MARGIN_TOP, ...expandedSectionSx }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: FIELD_STYLE_GRID_GAP,
                }}
              >
                {withScreenEditorFieldGuides(V2_FIELD_STYLES_CONFIG.styleFields ?? [], 'textField').map((styleConfig) => (
                  <StyleFieldRow
                    key={styleConfig.path}
                    config={styleConfig}
                    value={fieldStyleValues[styleConfig.path]}
                    onChange={(newValue) => handleStyleChange(styleConfig.path, newValue)}
                    disabled={disabled}
                    inputTestId={getStyleInputTestId(V2_FIELD_STYLES_CONFIG.label, styleConfig.label)}
                  />
                ))}
              </Box>

              {/* Keyboard Type — inside field settings */}
              <Box sx={{ mt: FIELD_STYLE_GRID_GAP }}>
                <Typography
                  variant="body2"
                  sx={{ ...mainFieldLabelEnabledSx, mb: FIELD_GAP_SMALL }}
                >
                  Keyboard Type
                </Typography>
                <FormControl size="small" disabled={disabled} variant="standard" fullWidth>
                  <Select
                    value={keyboardType}
                    onChange={handleKeyboardTypeChange as any}
                    displayEmpty
                    sx={fieldSelectSx}
                    data-testid={getInputTestId(SECTION_NAME, 'Keyboard Type')}
                  >
                    {V2_KEYBOARD_TYPE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value} sx={fieldMenuItemSx}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Auto-focus on appear — inside field settings */}
              <Box sx={{ mt: FIELD_STYLE_GRID_GAP }}>
                <Typography
                  variant="body2"
                  sx={{ ...mainFieldLabelEnabledSx, mb: FIELD_GAP_SMALL }}
                >
                  {V2_AUTO_FOCUS_CONFIG.label}
                </Typography>
                <FormControl size="small" disabled={disabled} variant="standard" fullWidth>
                  <Select
                    value={autoFocusValue}
                    onChange={(e) => handleAutoFocusChange(e.target.value)}
                    displayEmpty
                    sx={fieldSelectSx}
                    data-testid={getInputTestId(SECTION_NAME, V2_AUTO_FOCUS_CONFIG.label)}
                  >
                    {V2_AUTO_FOCUS_CONFIG.options?.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value} sx={fieldMenuItemSx}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Field padding — inside field settings */}
              <Box sx={{ mt: FIELD_STYLE_GRID_GAP }}>
                <PaddingElement
                  basePath="content.textField.layout.insets"
                  values={paddingValues}
                  onChange={handlePaddingChange}
                  disabled={disabled}
                  label="Field padding"
                  defaultExpanded={false}
                  guideKey="screenedit-text-field-padding"
                  data-testid="text-field-padding"
                />
              </Box>

              {/* Border margins — inside field settings */}
              <Box sx={{ mt: FIELD_STYLE_GRID_GAP }}>
                <PaddingElement
                  basePath="content.textField.appearance.borderMargins"
                  values={borderMarginsValues}
                  onChange={handlePaddingChange}
                  disabled={disabled}
                  label="Border margins"
                  defaultExpanded={false}
                  guideKey="screenedit-text-field-border-margins"
                  data-testid="text-field-border-margins"
                />
              </Box>
            </Box>
          </Collapse>
        </Box>

        {/* Placeholder */}
        <TextElement
          {...placeholderProps}
          onChange={(value) => handleFieldChange(V2_PLACEHOLDER_CONFIG, value)}
          onEnabledChange={(enabled) => handleEnabledChange(V2_PLACEHOLDER_CONFIG, enabled)}
          onStylesExpandToggle={() => toggleFieldExpansion(V2_PLACEHOLDER_CONFIG.path)}
          onStyleChange={handleStyleChange}
          disabled={disabled}
          data-testid={getFieldTestId(SECTION_NAME, V2_PLACEHOLDER_CONFIG.label)}
          checkboxTestId={getCheckboxTestId(SECTION_NAME, V2_PLACEHOLDER_CONFIG.label)}
          stylesToggleTestId={getStylesToggleTestId(SECTION_NAME, V2_PLACEHOLDER_CONFIG.label)}
          inputTestId={getInputTestId(SECTION_NAME, V2_PLACEHOLDER_CONFIG.label)}
        />

        {/* Validation Regex */}
        <Box sx={{ mb: FIELD_MARGIN_BOTTOM }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: FIELD_GAP_SMALL, mb: FIELD_GAP_SMALL }}>
            <Checkbox
              checked={hasValidationRegex}
              onChange={(e) => {
                if (e.target.checked) {
                  onChange('content.textField.validationRegex', '');
                } else {
                  onChange('content.textField.validationRegex', null);
                }
              }}
              disabled={disabled}
              size="small"
              icon={<Box sx={checkboxIconSx} />}
              checkedIcon={<Box sx={checkboxCheckedIconSx}>✓</Box>}
              sx={checkboxSx}
              data-testid={getCheckboxTestId(SECTION_NAME, V2_VALIDATION_REGEX_CONFIG.label)}
            />
            <Typography
              variant="body2"
              sx={hasValidationRegex ? mainFieldLabelEnabledSx : mainFieldLabelDisabledSx}
            >
              {V2_VALIDATION_REGEX_CONFIG.label}
            </Typography>
          </Box>
          <StyledTextField
            value={validationRegex}
            onChange={(value) => onChange('content.textField.validationRegex', value)}
            placeholder={V2_VALIDATION_REGEX_CONFIG.placeholder}
            disabled={disabled || !hasValidationRegex}
            size="small"
            fullWidth
            data-testid={getInputTestId(SECTION_NAME, V2_VALIDATION_REGEX_CONFIG.label)}
          />
        </Box>

        {/* Error Message */}
        <TextElement
          {...errorMessageProps}
          onChange={(value) => handleFieldChange(V2_ERROR_MESSAGE_CONFIG, value)}
          onEnabledChange={(enabled) => handleEnabledChange(V2_ERROR_MESSAGE_CONFIG, enabled)}
          onStylesExpandToggle={() => toggleFieldExpansion(V2_ERROR_MESSAGE_CONFIG.path)}
          onStyleChange={handleStyleChange}
          disabled={disabled}
          data-testid={getFieldTestId(SECTION_NAME, V2_ERROR_MESSAGE_CONFIG.label)}
          checkboxTestId={getCheckboxTestId(SECTION_NAME, V2_ERROR_MESSAGE_CONFIG.label)}
          stylesToggleTestId={getStylesToggleTestId(SECTION_NAME, V2_ERROR_MESSAGE_CONFIG.label)}
          inputTestId={getInputTestId(SECTION_NAME, V2_ERROR_MESSAGE_CONFIG.label)}
        />

        {/* Icon (optional LocalizedImage to the left of input) */}
        <ImageUploadElement
          fieldConfig={V2_ICON_CONFIG}
          value={iconUrl}
          templateValue={undefined}
          enabled={iconEnabled}
          locale={locale}
          onChange={handleIconChange}
          onEnabledChange={handleIconEnabledChange}
          onUpload={handleIconUpload}
          disabled={disabled}
          expandedStyles={expandedFields.has(V2_ICON_CONFIG.path)}
          onStylesExpandToggle={() => toggleFieldExpansion(V2_ICON_CONFIG.path)}
          styleValues={iconStyleValues}
          onStyleChange={handleStyleChange}
          data-testid={getFieldTestId(SECTION_NAME, V2_ICON_CONFIG.label)}
          checkboxTestId={getCheckboxTestId(SECTION_NAME, V2_ICON_CONFIG.label)}
          stylesToggleTestId={getStylesToggleTestId(SECTION_NAME, V2_ICON_CONFIG.label)}
        />
      </EditorGuideProvider>
      {subsectionPosition === 'last' && renderSubsections?.()}
    </SAccordion>
  );
};
